import type { ReactNode } from 'react';
import { Cluster, Table, THead, TBody, TR, TH, TD, EmptyState, Skeleton, PageHeader } from '../ui';

// EntityListPage (P16 §4) — the one list-screen template every list composes:
// header (title/description/actions) + filter bar + table + empty state, with the
// bulk-select slot reserved (renders nothing today; D39 plugs a selection toolbar
// in later, and every list inherits bulk ops for free). Two content modes:
//   - table mode: pass columns + rows (default),
//   - custom mode: pass children (e.g. a map/grid), which replaces the table.
// (Row virtualization is a seam — swap the <Table> body for a windowed list when
// a list gets large; the template contract does not change.)
//
// Header (story 2-147e — "collection" kind of P10 §2's header anatomy):
// composes `PageHeader` (story 2-127) rather than hand-rolling its own
// title/description/actions block, which is what this file did until now —
// a shape that had drifted textually near-identical to `PageHeader`'s own
// contract (title/description/actions) simply because this template
// predates that primitive (P16 §4 vs. 2-127). The two prop shapes map
// directly: `title: string`→`title: string`, `description?: string`→
// `description?: ReactNode` (a strict widening, not a mismatch), `actions?:
// ReactNode`→`actions?: ReactNode`. Composing PageHeader here means every
// future PageHeader improvement (chips, toolbar, a11y fix) reaches list
// pages for free instead of needing a second hand-rolled edit.
//
// The flat `bg-card` wrapper stays (kept from this file's own pre-2-147e
// shape, which 2-147d already converted off `.glass-card`) — this is a
// deliberate divergence from the 9 routes that compose bare `PageHeader`
// directly (Dashboard, Settings, …), not an oversight: P10 §1.3's surface-
// hierarchy rule names "a list-page header" by name as flat surface's own
// canonical example ("the DEFAULT the 'this looks like a card' instinct
// should reach for: a settings section, a metric card, a form wrapper, A
// LIST-PAGE HEADER, a collection card"). A collection page's header sits
// directly above a dense, self-contained content unit (the table/grid) that
// itself reads as one bounded object — the same "distinct content unit that
// needs separation from canvas" §1.3 defines the flat-surface role for — so
// wrapping it keeps the header visually paired with the list it introduces.
// A one-off page like Dashboard or Settings has no equivalent adjacent
// object to separate FROM, which is why those 9 routes correctly stay bare.
// What actually converges here is the code path (one shared PageHeader
// composition, zero duplicated title/description/actions markup) — not
// pixel-identical chrome across every route in the app, which §1.3 does not
// call for and the census does not name as a defect.
//
// Story 2-147tb: dropped the `search` prop and its hand-rolled `<Input
// type="search">` render path. Confirmed by grepping every real usage tree-
// wide (`search={`): zero — every actual collection page already composes
// `FilterBar`'s own `keyword` slot (via the `filters` prop) for keyword
// search instead, so the two paths were never really parallel options, one
// was just unused code duplicating `FilterBar`'s own canonical keyword-
// search rendering. Removing it is this story's "one canonical rendering
// path" done-when applied to the template itself, not only its consumers.

export interface EntityColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

export interface EntityListPageProps<T> {
  title: string;
  description?: string;
  actions?: ReactNode;
  filters?: ReactNode;
  // Reserved bulk-select slot (D39). Renders nothing until a feature provides it.
  bulkActions?: ReactNode;
  isLoading?: boolean;
  columns?: EntityColumn<T>[];
  rows?: T[];
  rowKey?: (row: T) => string;
  onRowClick?: (row: T) => void;
  children?: ReactNode;
  // Below-the-content slot (story 2-76): where a list's paging control lives —
  // the `useCursorList()` "Load more" button, or an explicit pager where URL
  // state matters (G-25). Rendered after the table/children and never in the
  // loading or empty state, so a list with nothing in it shows one message, not
  // a message plus a dead control.
  footer?: ReactNode;
  // The empty state's own call to action. CONTRACT (story 4-59): `actionLabel`
  // must NOT repeat the label of any control passed in `actions` — when the list
  // is empty BOTH render, so an identical label puts two visible controls with
  // the same accessible name on the page. That is an a11y defect (a screen-reader
  // rotor cannot tell them apart) and it breaks every spec that addresses the
  // control by role+name, which is how it was found: Playwright strict mode
  // resolved `/new inspection type/i` to 2 elements and took 7 checks down with
  // it. `actions` is an opaque ReactNode, so this template CANNOT detect the
  // duplication for you — prefer first-run copy here ("Create your first X")
  // and leave the repeat-action verb to the header.
  empty: { title: string; description?: string; actionLabel?: string; onAction?: () => void };
}

export function EntityListPage<T>(props: EntityListPageProps<T>): JSX.Element {
  const { title, description, actions, filters, bulkActions, isLoading, columns, rows, rowKey, onRowClick, children, footer, empty } = props;

  const hasFilterBar = Boolean(filters || bulkActions);
  const isEmpty = !isLoading && !children && (!rows || rows.length === 0);

  return (
    <div className="space-y-6">
      <div className="rounded-hairline border border-border bg-card p-6" data-testid="entity-list-header">
        <PageHeader title={title} description={description} actions={actions} />
      </div>

      {hasFilterBar && (
        <Cluster gap={3}>
          {filters}
          {/* Bulk-select slot (reserved, D39): renders nothing until provided. */}
          {bulkActions}
        </Cluster>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </div>
      ) : isEmpty ? (
        <EmptyState
          title={empty.title}
          description={empty.description ?? ''}
          {...(empty.actionLabel ? { actionLabel: empty.actionLabel } : {})}
          {...(empty.onAction ? { onAction: empty.onAction } : {})}
        />
      ) : children ? (
        children
      ) : (
        <Table aria-label={title}>
          <THead>
            <TR>
              {(columns ?? []).map((col) => (
                <TH key={col.key}>{col.header}</TH>
              ))}
            </TR>
          </THead>
          <TBody>
            {(rows ?? []).map((row, index) => (
              <TR
                key={rowKey ? rowKey(row) : String(index)}
                {...(onRowClick ? { onClick: () => onRowClick(row), className: 'cursor-pointer' } : {})}
              >
                {(columns ?? []).map((col) => (
                  <TD key={col.key} {...(col.className ? { className: col.className } : {})}>
                    {col.render(row)}
                  </TD>
                ))}
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {!isLoading && !isEmpty && footer}
    </div>
  );
}
